import { useState } from 'react'
import RoutesMap from 'route_control/RouteControl'
import { Form, InputGroup, Button, Card } from 'react-bootstrap'
import { Input } from 'antd'
import { useFormik } from 'formik'
import { ForgotPasswordStep1Schema, ForgotPasswordResetSchema } from './ValidationSchema'
import { KeyRound, UserRound, Eye, EyeOff, Smartphone, Mail, ArrowLeft } from 'lucide-react'
import { toast } from 'react-toastify'
import logo from 'assets/images/express-logo.png'
import { ButtonLoader } from 'components/loader'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { forgotPasswordSendOtp, forgotPasswordVerifyOtp, forgotPasswordReset } from '../../../redux/slices/assets'

const STEPS = [
  { key: 'account', label: 'Account' },
  { key: 'otp', label: 'Verify OTP' },
  { key: 'password', label: 'New password' },
]

export default function ForgotPasswordForm(props) {

  const { setTogggle } = props

  const [step, setStep] = useState(0)
  const [spinner, setSpinner] = useState(false)
  const [otp, setOtp] = useState('')
  const [otpError, setOtpError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const [ctx, setCtx] = useState({
    username: '',
    verificationMethod: '',
  })

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const step1Formik = useFormik({
    initialValues: { username: '', verificationMethod: '' },
    validationSchema: ForgotPasswordStep1Schema,

    onSubmit: (values) => {
      setSpinner(true)
      dispatch(forgotPasswordSendOtp({
        user_name: values.username.trim(),
        verification_method: values.verificationMethod,
      })).then((res) => {
        setSpinner(false)
        const payload = res?.payload
        if (payload?.status === 200) {
          setCtx({
            username: values.username.trim(),
            verificationMethod: values.verificationMethod,
          })
          toast.success(payload?.data?.message, { position: 'bottom-right' })
          setOtp('')
          setOtpError('')
          setStep(1)
        } else if (payload?.status === 400) {
          toast.error(payload?.data?.message, { position: 'bottom-right' })
        } else {
          toast.error("Something went wrong!!..", { position: 'bottom-right' })
        }
      })
    },
  })

  const resetFormik = useFormik({
    initialValues: { newPassword: '', confirmPassword: '' },
    validationSchema: ForgotPasswordResetSchema,

    onSubmit: (values, action) => {
      setSpinner(true)
      dispatch(forgotPasswordReset({
        user_name: ctx.username,
        user_otp: otp,
        verification_method: ctx.verificationMethod,
        user_password: values.newPassword,
      })).then((res) => {
        setSpinner(false)
        const payload = res?.payload
        if (payload?.status === 200) {
          toast.success(payload?.data?.message, { position: 'bottom-right' })
          action.resetForm()
          setOtp('')
          setStep(0)
          setTogggle(false)
          navigate(RoutesMap.LOGIN.path)
        } else if (payload?.status === 400) {
          toast.error(payload?.data?.message, { position: 'bottom-right' })
        } else {
          toast.error("Something went wrong!!..", { position: 'bottom-right' })
        }
      })
    },
  })

  const handleVerifyOtp = () => {
    setOtpError('')
    const digits = otp.replace(/\D/g, '')
    if (digits.length !== 6) {
      setOtpError('Enter the complete 6-digit code.')
      return
    }
    setSpinner(true)
    dispatch(forgotPasswordVerifyOtp({
      user_name: ctx.username,
      user_otp: digits,
      verification_method: ctx.verificationMethod,
    })).then((res) => {
      setSpinner(false)
      const payload = res?.payload
      if (payload?.status === 200) {
        toast.success(payload?.data?.message, { position: 'bottom-right' })
        setOtp(digits)
        setStep(2)
        resetFormik.resetForm()
      } else if (payload?.status === 400) {
        toast.error(payload?.data?.message, { position: 'bottom-right' })
      } else {
        toast.error("Something went wrong!!..", { position: 'bottom-right' })
      }
    })
  }

  const handleResendOtp = () => {
    if (!ctx.username || !ctx.verificationMethod) return
    setSpinner(true)
    dispatch(forgotPasswordSendOtp({
      user_name: ctx.username,
      verification_method: ctx.verificationMethod,
    })).then((res) => {
      setSpinner(false)
      const payload = res?.payload
      if (payload?.status === 200) {
        toast.success(payload?.data?.message, { position: 'bottom-right' })
        setOtp('')
        setOtpError('')
      } else {
        toast.error(payload?.data?.message, { position: 'bottom-right' })
      }
    })
  }

  const goBackToLogin = () => {
    setStep(0)
    setOtp('')
    setOtpError('')
    step1Formik.resetForm()
    resetFormik.resetForm()
    setTogggle(false)
  }

  const stepIndicator = (
    <div className="d-flex justify-content-center align-items-center gap-1 mb-3 flex-wrap">
      {STEPS.map((s, i) => (
        <span key={s.key} className="d-flex align-items-center">
          <span
            className="rounded-pill px-2 py-1"
            style={{
              fontSize: '11px',
              fontWeight: 600,
              backgroundColor: i === step ? '#006972' : i < step ? '#e8eeed' : '#f1f3f5',
              color: i === step ? '#fff' : '#6c757d',
            }}
          >
            {i + 1}. {s.label}
          </span>
          {i < STEPS.length - 1 ? <span className="text-muted mx-1" style={{ fontSize: '10px' }}>›</span> : null}
        </span>
      ))}
    </div>
  )

  return (
    <Card className="h-100 border-0 shadow" style={{ borderRadius: '16px', backgroundColor: '#ffffff', overflow: 'hidden' }}>
      <Form
        className="h-100 d-flex flex-column"
        onSubmit={(e) => {
          e.preventDefault()
          if (step === 0) step1Formik.handleSubmit()
          if (step === 2) resetFormik.handleSubmit()
        }}
      >
        <div className="text-start px-4 pt-3">
          <Button
            variant="link"
            className="p-0 text-decoration-none text-muted d-inline-flex align-items-center gap-1"
            style={{ fontSize: '13px' }}
            type="button"
            onClick={goBackToLogin}
          >
            <ArrowLeft size={16} />
            Back to Sign In
          </Button>
        </div>

        <Card.Body className="px-4 px-sm-5 d-flex flex-column justify-content-center pb-4">
          <div className="text-center mb-2">
            <img src={logo} alt="logo" className="img-fluid mb-2" style={{ height: '60px', objectFit: 'contain' }} />
            <h4 className="fw-bold mb-1" style={{ color: '#001529' }}>
              Forgot password
            </h4>
            <p className="text-muted" style={{ fontSize: '14px' }}>
              {step === 0 && 'Enter your username and choose how we should send your verification code.'}
              {step === 1 && `We sent a 6-digit code to your ${ctx.verificationMethod === 'mobile' ? 'mobile number' : 'email'}.`}
              {step === 2 && 'Choose a strong new password for your account.'}
            </p>
          </div>

          {stepIndicator}

          {step === 0 && (
            <>
              <div className="mb-2">
                <Form.Label className="fw-semibold mb-1 text-dark" style={{ fontSize: '13px' }}>
                  Username
                </Form.Label>
                <InputGroup
                  className="rounded-3 shadow-sm"
                  style={{ border: '1px solid #e1e5eb', overflow: 'hidden', backgroundColor: '#f9fafb' }}
                >
                  <InputGroup.Text className="bg-transparent border-0 text-muted px-3">
                    <UserRound size={18} strokeWidth={2} />
                  </InputGroup.Text>
                  <Form.Control
                    className="login-input border-0 bg-transparent shadow-none px-0"
                    placeholder="Enter your username"
                    name="username"
                    value={step1Formik.values.username}
                    onChange={step1Formik.handleChange}
                    onBlur={step1Formik.handleBlur}
                    style={{ fontSize: '14px', height: '45px' }}
                  />
                </InputGroup>
                {step1Formik.errors.username && step1Formik.touched.username ? (
                  <div className="text-danger mt-1 ms-1" style={{ fontSize: '12px' }}>
                    {step1Formik.errors.username}
                  </div>
                ) : null}
              </div>

              <div className="mb-2">
                <Form.Label className="fw-semibold mb-2 text-dark" style={{ fontSize: '13px' }}>
                  Verification method
                </Form.Label>
                <div className="d-flex flex-column gap-2">
                  {[
                    { value: 'mobile', label: 'Mobile', desc: 'Receive code via SMS', Icon: Smartphone },
                    { value: 'email', label: 'Email', desc: 'Receive code via email', Icon: Mail },
                  ].map(({ value, label, desc, Icon }) => {
                    const selected = step1Formik.values.verificationMethod === value
                    return (
                      <button
                        key={value}
                        type="button"
                        className="d-flex align-items-center gap-3 text-start w-100 border rounded-3 px-3 py-2"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selected ? '#f0f5f4' : '#f9fafb',
                          borderColor: selected ? '#006972' : '#e1e5eb',
                          borderWidth: selected ? '2px' : '1px',
                          transition: 'border-color 0.2s, background 0.2s',
                        }}
                        onClick={() => step1Formik.setFieldValue('verificationMethod', value)}
                      >
                        <span
                          className="d-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            width: 40,
                            height: 40,
                            backgroundColor: selected ? '#006972' : '#e9ecef',
                            color: selected ? '#fff' : '#6c757d',
                          }}
                        >
                          <Icon size={20} />
                        </span>
                        <span>
                          <span className="fw-semibold d-block text-dark" style={{ fontSize: '14px' }}>
                            {label}
                          </span>
                          <span className="text-muted" style={{ fontSize: '12px' }}>
                            {desc}
                          </span>
                        </span>
                      </button>
                    )
                  })}
                </div>
                {step1Formik.errors.verificationMethod && step1Formik.touched.verificationMethod ? (
                  <div className="text-danger mt-1 ms-1" style={{ fontSize: '12px' }}>
                    {step1Formik.errors.verificationMethod}
                  </div>
                ) : null}
              </div>

              <div className="mt-2 text-center">
                <Button
                  type="submit"
                  className="w-100 login_save_btn shadow text-uppercase fw-bold rounded-3"
                  style={{ letterSpacing: '1px' }}
                  disabled={spinner}
                >
                  {spinner ? <ButtonLoader /> : 'Send verification code'}
                </Button>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="px-2 py-3 bg-light rounded-4 border border-light mb-2">
                <Form.Label className="fw-bold mb-2 d-block text-center" style={{ color: '#001529', fontSize: '14px' }}>
                  Enter 6-digit OTP
                </Form.Label>
                <p className="text-muted small text-center mb-3">
                  Enter the code we sent. It may take a minute to arrive.
                </p>
                <div className="d-flex justify-content-center">
                  <Input.OTP
                    size="large"
                    length={6}
                    value={otp}
                    onChange={(value) => {
                      setOtp(value || '')
                      setOtpError('')
                    }}
                  />
                </div>
                {otpError ? (
                  <div className="text-danger text-center mt-2" style={{ fontSize: '12px' }}>
                    {otpError}
                  </div>
                ) : null}
              </div>

              <div className="text-center mb-2">
                <Button variant="link" className="p-0 text-decoration-none" style={{ fontSize: '13px' }} type="button" onClick={handleResendOtp} disabled={spinner}>
                  Resend code
                </Button>
              </div>

              <div className="d-flex flex-column gap-2">
                <Button
                  type="button"
                  className="w-100 login_save_btn shadow text-uppercase fw-bold rounded-3"
                  style={{ letterSpacing: '1px' }}
                  disabled={spinner || otp.replace(/\D/g, '').length < 6}
                  onClick={handleVerifyOtp}
                >
                  {spinner ? <ButtonLoader /> : 'Verify & continue'}
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  className="w-100 rounded-3 fw-semibold"
                  style={{ height: '45px' }}
                  disabled={spinner}
                  onClick={() => {
                    setStep(0)
                    setOtp('')
                    setOtpError('')
                    step1Formik.setValues({
                      username: ctx.username,
                      verificationMethod: ctx.verificationMethod,
                    })
                  }}
                >
                  Back
                </Button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="mb-2">
                <Form.Label className="fw-semibold mb-1 text-dark" style={{ fontSize: '13px' }}>
                  New Password
                </Form.Label>
                <InputGroup
                  className="rounded-3 shadow-sm"
                  style={{ border: '1px solid #e1e5eb', overflow: 'hidden', backgroundColor: '#f9fafb' }}
                >
                  <InputGroup.Text className="bg-transparent border-0 text-muted px-3">
                    <KeyRound size={18} strokeWidth={2} />
                  </InputGroup.Text>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    className="login-input border-0 bg-transparent shadow-none px-0"
                    placeholder="Enter new password"
                    name="newPassword"
                    value={resetFormik.values.newPassword}
                    onChange={resetFormik.handleChange}
                    onBlur={resetFormik.handleBlur}
                    style={{ fontSize: '14px', height: '45px' }}
                  />
                  <InputGroup.Text
                    className="bg-transparent border-0 text-muted px-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </InputGroup.Text>
                </InputGroup>
                {resetFormik.errors.newPassword && resetFormik.touched.newPassword ? (
                  <div className="text-danger mt-1 ms-1" style={{ fontSize: '12px' }}>
                    {resetFormik.errors.newPassword}
                  </div>
                ) : null}
              </div>

              <div className="mb-2">
                <Form.Label className="fw-semibold mb-1 text-dark" style={{ fontSize: '13px' }}>
                  Confirm Password
                </Form.Label>
                <InputGroup
                  className="rounded-3 shadow-sm"
                  style={{ border: '1px solid #e1e5eb', overflow: 'hidden', backgroundColor: '#f9fafb' }}
                >
                  <InputGroup.Text className="bg-transparent border-0 text-muted px-3">
                    <KeyRound size={18} strokeWidth={2} />
                  </InputGroup.Text>
                  <Form.Control
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="login-input border-0 bg-transparent shadow-none px-0"
                    placeholder="Confirm new password"
                    name="confirmPassword"
                    value={resetFormik.values.confirmPassword}
                    onChange={resetFormik.handleChange}
                    onBlur={resetFormik.handleBlur}
                    style={{ fontSize: '14px', height: '45px' }}
                  />
                  <InputGroup.Text
                    className="bg-transparent border-0 text-muted px-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </InputGroup.Text>
                </InputGroup>
                {resetFormik.errors.confirmPassword && resetFormik.touched.confirmPassword ? (
                  <div className="text-danger mt-1 ms-1" style={{ fontSize: '12px' }}>
                    {resetFormik.errors.confirmPassword}
                  </div>
                ) : null}
              </div>

              <div className="d-flex flex-column gap-2 mt-2">
                <Button
                  type="submit"
                  className="w-100 login_save_btn shadow text-uppercase fw-bold rounded-3"
                  style={{ letterSpacing: '1px' }}
                  disabled={spinner}
                >
                  {spinner ? <ButtonLoader /> : 'Reset password'}
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  className="w-100 rounded-3 fw-semibold"
                  style={{ height: '45px' }}
                  disabled={spinner}
                  onClick={() => setStep(1)}
                >
                  Back
                </Button>
              </div>
            </>
          )}
        </Card.Body>

        <Card.Footer className="bg-light border-top-0 px-4 py-2 text-center">
          <span style={{ fontSize: '12px' }} className="text-muted">
            For security, never share your verification code with anyone.
          </span>
        </Card.Footer>
      </Form>
    </Card>
  )
}